import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Button, TextField, FormControl, InputLabel,
  Select, MenuItem, Tooltip, Chip, Typography, Collapse, Divider, Badge, InputAdornment,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Tune as TuneIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { FilterOptions, KpiData, PeriodStatus, SortField, Order } from '../types';
import { UseColumnVisibilityReturn } from '../hooks/useColumnVisibility';
import { API_BASE } from '../constants';
import ColumnVisibilityMenu from './ColumnVisibilityMenu';

interface JournalToolbarProps {
  pharmacyGuid: string;

  // ── Filter state ──
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  selectedMonth: string;
  setSelectedMonth: (v: string) => void;
  selectedCategory: string;
  setSelectedCategory: (v: string) => void;
  selectedManager: string;
  setSelectedManager: (v: string) => void;
  expiryFrom: string;
  setExpiryFrom: (v: string) => void;
  expiryTo: string;
  setExpiryTo: (v: string) => void;

  // ── Options & sort ──
  filterOptions: FilterOptions;
  monthsList: { value: string; label: string }[];
  filterParams: Record<string, string>;
  orderBy: SortField;
  order: Order;

  // ── KPI & Period ──
  kpi: KpiData | null;
  periodStatus: PeriodStatus | null;

  // ── Column Visibility ──
  columnVisibility: Pick<UseColumnVisibilityReturn, 'visibleColumns' | 'toggle' | 'showAll' | 'reset'>;

  // ── Callbacks ──
  onReset: () => void;
  onRefresh: () => void;
  onAdd: () => void;
}

const JournalToolbar: React.FC<JournalToolbarProps> = ({
  pharmacyGuid,
  searchTerm, setSearchTerm,
  selectedMonth, setSelectedMonth,
  selectedCategory, setSelectedCategory,
  selectedManager, setSelectedManager,
  expiryFrom, setExpiryFrom,
  expiryTo, setExpiryTo,
  filterOptions, monthsList, filterParams, orderBy, order,
  kpi, periodStatus,
  columnVisibility,
  onReset, onRefresh, onAdd,
}) => {
  const theme = useTheme();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const advancedActiveCount = useMemo(() => {
    let n = 0;
    if (selectedCategory !== 'all') n += 1;
    if (selectedManager !== 'all') n += 1;
    if (expiryFrom || expiryTo) n += 1;
    return n;
  }, [selectedCategory, selectedManager, expiryFrom, expiryTo]);

  const hasChips = selectedCategory !== 'all' || selectedManager !== 'all' || expiryFrom || expiryTo;
  const canCreate = periodStatus?.canCreate ?? true;

  const handleExport = () => {
    const version = process.env.REACT_APP_DOC_VERSION || '1.0.9';
    const params = new URLSearchParams({
      version,
      pharmacyGuid,
      ...filterParams,
      orderBy: String(orderBy),
      order: String(order),
    });
    window.open(`${API_BASE}/journal-user/export?${params.toString()}`, '_blank');
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      {/* ── Основная строка ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>

        {/* Иконка Фильтр */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', flexShrink: 0 }}>
          <FilterListIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>Фильтр:</Typography>
        </Box>

        {/* Поиск */}
        <TextField
          size="small"
          placeholder="Наименование, серия, код…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200, flex: 1, maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Период */}
        <FormControl size="small" sx={{ minWidth: 155 }}>
          <InputLabel>Период</InputLabel>
          <Select value={selectedMonth} label="Период" onChange={e => setSelectedMonth(e.target.value)}>
            <MenuItem value="all">Все периоды</MenuItem>
            {monthsList.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </Select>
        </FormControl>

        {/* Доп. фильтры */}
        <Badge badgeContent={advancedActiveCount} color="primary">
          <Button
            variant={advancedOpen ? 'contained' : 'outlined'}
            color={advancedOpen ? 'primary' : 'inherit'}
            size="small"
            onClick={() => setAdvancedOpen(o => !o)}
            sx={{ height: 32, minWidth: 32, width: 32, p: 0, borderRadius: 1.5, borderColor: 'rgba(0,0,0,0.23)' }}
          >
            <TuneIcon fontSize="small" />
          </Button>
        </Badge>

        <Box sx={{ flexGrow: 1 }} />

        {/* ── Группа действий ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>

          <ColumnVisibilityMenu
            visibleColumns={columnVisibility.visibleColumns}
            toggle={columnVisibility.toggle}
            showAll={columnVisibility.showAll}
            reset={columnVisibility.reset}
          />

          <Tooltip title="Сбросить все фильтры">
            <Button
              variant="outlined" color="inherit" size="small" onClick={onReset}
              sx={{ height: 32, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, borderColor: 'rgba(0,0,0,0.23)', '&:hover': { borderColor: '#b0b0b0', bgcolor: 'action.hover' } }}
            >
              Сброс
            </Button>
          </Tooltip>

          <Tooltip title="Обновить данные">
            <Button
              variant="contained" size="small" startIcon={<RefreshIcon />} onClick={onRefresh}
              sx={{ height: 32, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, px: 2 }}
            >
              Обновить
            </Button>
          </Tooltip>

          <Tooltip title="Скачать в Excel">
            <span style={{ display: 'inline-flex' }}>
              <Button
                variant="outlined" size="small" startIcon={<DownloadIcon />}
                onClick={handleExport} disabled={kpi?.count === 0}
                sx={{
                  height: 32, textTransform: 'none', fontWeight: 600, borderRadius: 1.5,
                  color: 'success.main', borderColor: 'success.main',
                  '&:hover': { borderColor: 'success.dark', bgcolor: alpha(theme.palette.success.main, 0.04) },
                }}
              >
                Excel
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={canCreate ? 'Добавить запись' : (periodStatus?.periodName ?? 'Период ввода закрыт')}>
            <span style={{ display: 'inline-flex' }}>
              <Button
                variant="contained" color="primary" size="small"
                startIcon={<AddIcon />} onClick={onAdd} disabled={!canCreate}
                sx={{ height: 32, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, px: 2 }}
              >
                Добавить
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Чипы активных фильтров ── */}
      {hasChips && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {selectedCategory !== 'all' && (
            <Chip size="small" label={selectedCategory} onDelete={() => setSelectedCategory('all')}
              sx={{ bgcolor: 'rgba(139,92,246,0.08)', color: '#7c3aed', fontWeight: 600 }} />
          )}
          {selectedManager !== 'all' && (
            <Chip size="small" label={selectedManager} onDelete={() => setSelectedManager('all')}
              sx={{ bgcolor: 'rgba(236,72,153,0.08)', color: '#db2777', fontWeight: 600 }} />
          )}
          {(expiryFrom || expiryTo) && (
            <Chip size="small" label={`Срок: ${expiryFrom || '?'} — ${expiryTo || '?'}`}
              onDelete={() => { setExpiryFrom(''); setExpiryTo(''); }}
              sx={{ bgcolor: 'rgba(245,158,11,0.08)', color: '#d97706', fontWeight: 600 }} />
          )}
        </Box>
      )}

      {/* ── Дополнительные фильтры ── */}
      {advancedOpen && (
        <Box>
          <Divider sx={{ my: 1.25 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Категория</InputLabel>
              <Select value={selectedCategory} label="Категория" onChange={e => setSelectedCategory(e.target.value)}>
                <MenuItem value="all">Все категории</MenuItem>
                {filterOptions.categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Менеджер</InputLabel>
              <Select value={selectedManager} label="Менеджер" onChange={e => setSelectedManager(e.target.value)}>
                <MenuItem value="all">Все менеджеры</MenuItem>
                {filterOptions.managers.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" type="date" label="Срок годн. от"
              value={expiryFrom} onChange={e => setExpiryFrom(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <TextField size="small" type="date" label="Срок годн. до"
              value={expiryTo} onChange={e => setExpiryTo(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 150 }}
              inputProps={{ min: expiryFrom || undefined }} />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default JournalToolbar;
